/**
 * Renders a menu of buttons that can be navigated with the arrow keys and
 * closed by tabbing or clicking out.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {findDOMNode} from "react-dom";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";

type Props = {
    buttons: Array<{
        // A unique key for this button.
        key: string;

        // A function that renders the button. The first argument is a ref
        // callback that should be passed to the button's ref prop.
        render: (
            ref?: (node: React.Component | HTMLElement | null) => void,
        ) => React.ReactElement;
    }>;

    // The index of the button that should be focused when the menu is opened.
    // Only read when the menu first opens.
    initialFocusedButtonIndex: number;

    // Called when the menu is closed. If the menu is closed by clicking outside
    // of it, the event is passed to onClose. This callback should be generated
    // by useSelectMenuState.
    // **NOTE**: if clickOutEvent is not passed, this callback needs to focus
    // the button that opens this menu.
    onClose: (clickOutEvent: Event | null) => void;
};

export default function SelectMenu({
    buttons,
    initialFocusedButtonIndex,
    onClose,
}: Props): React.ReactElement {
    const [focusedButtonIndex, setFocusedButtonIndex] = React.useState(
        initialFocusedButtonIndex,
    );
    const shouldFocusButtonRef = React.useRef(true);
    const menuRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        const handleInteract = (e: Event) => {
            if (
                !(e.target instanceof Node) ||
                menuRef.current?.contains(e.target)
            ) {
                return;
            }
            onClose(e);
        };
        document.addEventListener("mouseup", handleInteract);
        document.addEventListener("touchend", handleInteract);
        return () => {
            document.removeEventListener("mouseup", handleInteract);
            document.removeEventListener("touchend", handleInteract);
        };
    }, [onClose]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                e.stopPropagation();
                setFocusedButtonIndex(
                    (focusedButtonIndex + buttons.length - 1) % buttons.length,
                );
                shouldFocusButtonRef.current = true;
                break;
            case "ArrowDown":
                e.preventDefault();
                e.stopPropagation();
                setFocusedButtonIndex(
                    (focusedButtonIndex + 1) % buttons.length,
                );
                shouldFocusButtonRef.current = true;
                break;
            case "Tab":
                // we intentionally don't preventDefault or stopPropagation
                // here. in the onClose callback, we refocus the dropdown
                // button that opens this menu. after that, the tab event
                // will continue to propagate and the next focusable element
                // after the dropdown button (or before in the case of shift+tab)
                // will be focused.
                onClose(null);
                break;
        }
    };

    const onKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onClose(null);
        }
    };

    const focusRef = (button) => {
        const element =
            button instanceof HTMLElement ? button : findDOMNode(button);
        if (shouldFocusButtonRef.current && element instanceof HTMLElement) {
            element.focus();
            shouldFocusButtonRef.current = false;
        }
    };

    return (
        <View
            role="menu"
            style={styles.menu}
            ref={menuRef}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
        >
            {buttons.map(({key, render}, index) => (
                <React.Fragment key={key}>
                    {render(
                        index === focusedButtonIndex ? focusRef : undefined,
                    )}
                </React.Fragment>
            ))}
        </View>
    );
}

export type SelectMenuState = {
    // Whether the menu is open.
    isOpen: boolean;

    // Opens or closes the menu. Pass this as the onClick handler for the
    // element that should open the menu.
    onToggle: () => void;

    // Closes the menu. Pass this as the onClose prop to SelectMenu, or call it
    // to programmatically close the menu (pass null as the arg in that case).
    onClose: (clickOutEvent: Event | null) => void;
};

export function useSelectMenuState(
    triggerRef: React.RefObject<HTMLButtonElement | null>,
): SelectMenuState {
    const [isOpen, setIsOpen] = React.useState(false);
    const onToggle = React.useCallback(() => setIsOpen((prev) => !prev), []);
    const onClose = React.useCallback(
        (clickOutEvent: Event | null) => {
            if (
                clickOutEvent?.target instanceof Node &&
                triggerRef.current?.contains(clickOutEvent.target)
            ) {
                return;
            }
            setIsOpen(false);
            if (clickOutEvent == null) {
                triggerRef.current?.focus();
            }
        },
        [triggerRef],
    );

    return {
        isOpen,
        onToggle,
        onClose,
    };
}

const styles = StyleSheet.create({
    menu: {
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        border: `1px solid ${color.offBlack16}`,
        backgroundColor: color.white,
    },
});
